export const DOORWAY_AI_ROUTE = "/.doorway/ai/v1/generate" as const;

export interface ManagedAiFixedRouteSourceUsage {
  callCount: number;
  validPostCount: number;
}

export function analyzeManagedAiFixedRouteSource(source: string): ManagedAiFixedRouteSourceUsage {
  const routeCall = /\bfetch\s*\(\s*(["'])\/\.doorway\/ai\/v1\/generate\1\s*[,)]/g;
  let callCount = 0;
  let validPostCount = 0;
  for (const match of source.matchAll(routeCall)) {
    if (match.index === undefined) continue;
    callCount += 1;
    if (!match[0].trimEnd().endsWith(",")) continue;
    let optionsStart = match.index + match[0].length;
    while (/\s/.test(source[optionsStart] ?? "")) optionsStart += 1;
    const options = source[optionsStart] === "{" ? balancedObjectSource(source, optionsStart) : null;
    if (
      options
      && /\bmethod\s*:\s*["']POST["']/i.test(options)
      && /["']?content-type["']?\s*:\s*["']application\/json["']/i.test(options)
      && /\bbody\s*:/.test(options)
    ) validPostCount += 1;
  }
  return { callCount, validPostCount };
}

function balancedObjectSource(source: string, start: number): string | null {
  if (source[start] !== "{") return null;
  let depth = 0;
  let quote: "\"" | "'" | "`" | null = null;
  let escaped = false;
  for (let index = start; index < source.length; index += 1) {
    const character = source[index]!;
    if (quote) {
      if (escaped) escaped = false;
      else if (character === "\\") escaped = true;
      else if (character === quote) quote = null;
      continue;
    }
    if (character === "\"" || character === "'" || character === "`") {
      quote = character;
      continue;
    }
    if (character === "{") depth += 1;
    else if (character === "}") {
      depth -= 1;
      if (depth === 0) return source.slice(start, index + 1);
    }
  }
  return null;
}

export const MANAGED_AI_PROVIDERS = ["anthropic", "openai", "google"] as const;
export type ManagedAIProvider = (typeof MANAGED_AI_PROVIDERS)[number];

export const MANAGED_AI_RESPONSE_MODES = ["text", "json"] as const;
export type ManagedAIResponseMode = (typeof MANAGED_AI_RESPONSE_MODES)[number];

export const MANAGED_AI_ERROR_CODES = [
  "AI_SETUP_REQUIRED",
  "AI_PAUSED",
  "AI_CAP_REACHED",
  "INVALID_REQUEST",
  "UNSUPPORTED_FIELD",
  "INVALID_MESSAGES",
  "REQUEST_TOO_LARGE",
  "INVALID_INSTRUCTIONS",
  "INVALID_RESPONSE_MODE",
  "INVALID_OUTPUT_LIMIT",
  "METHOD_NOT_ALLOWED",
  "JSON_REQUIRED",
  "PROVIDER_UNAVAILABLE",
  "PROVIDER_RESPONSE_INVALID",
  "INTERNAL_ERROR",
] as const;
export type ManagedAIErrorCode = (typeof MANAGED_AI_ERROR_CODES)[number];

export const MANAGED_AI_LIMITS = {
  maxMessages: 32,
  maxMessageCharacters: 16_384,
  maxInstructionsCharacters: 16_384,
  maxTotalInputCharacters: 65_536,
  maxOutputTokens: 4_096,
  maxResponseCharacters: 262_144,
} as const;

// JSON can encode one UTF-16 code unit as six bytes (for example, "\\u0000").
// The fixed overhead leaves room for the provider response envelope and usage data.
export const MANAGED_AI_MAX_TRANSPORT_RESPONSE_BYTES =
  MANAGED_AI_LIMITS.maxResponseCharacters * 6 + 16 * 1024;

// A valid request may encode every allowed UTF-16 code unit as a six-byte JSON
// escape (for example, "\\u0000"). Keep transport parsing above that worst case.
export const MANAGED_AI_MAX_TRANSPORT_REQUEST_BYTES =
  MANAGED_AI_LIMITS.maxTotalInputCharacters * 6 + 16 * 1024;

export type ManagedAIMessageRole = "user" | "assistant";

export interface ManagedAIMessage {
  role: ManagedAIMessageRole;
  content: string;
}

export interface ManagedAIRequest {
  messages: ManagedAIMessage[];
  instructions?: string;
  responseMode?: ManagedAIResponseMode;
  maxOutputTokens?: number;
}

export type JsonValue = null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue };

export interface ManagedAIUsage {
  inputTokens: number;
  outputTokens: number;
}

export interface ManagedAITextOutput {
  type: "text";
  text: string;
}

export interface ManagedAIJsonOutput {
  type: "json";
  value: JsonValue;
}

export interface ManagedAIResponse {
  requestId: string;
  output: ManagedAITextOutput | ManagedAIJsonOutput;
  finishReason: "stop" | "length";
  usage: ManagedAIUsage;
}

export interface ManagedAIErrorResponse {
  code: ManagedAIErrorCode;
  message: string;
  retryable: boolean;
}

export class ManagedAIContractError extends Error {
  readonly code: ManagedAIErrorCode;

  constructor(code: ManagedAIErrorCode, message: string) {
    super(message);
    this.name = "ManagedAIContractError";
    this.code = code;
  }
}

function invalid(code: ManagedAIErrorCode, message: string): never {
  throw new ManagedAIContractError(code, message);
}

function recordAt(value: unknown, code: ManagedAIErrorCode, message: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    invalid(code, message);
  }
  return value as Record<string, unknown>;
}

function exactKeys(
  record: Record<string, unknown>,
  allowed: readonly string[],
  code: ManagedAIErrorCode,
): void {
  const allowedSet = new Set(allowed);
  if (Object.keys(record).some((key) => !allowedSet.has(key))) {
    invalid(code, "The request contains an unsupported field.");
  }
}

function nonEmptyBoundedText(value: unknown, maximum: number, code: ManagedAIErrorCode, message: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    invalid(code, message);
  }
  if (value.length > maximum) {
    invalid("REQUEST_TOO_LARGE", "The request exceeds Doorway's managed AI input limit.");
  }
  return value;
}

export function parseManagedAIRequest(value: unknown): ManagedAIRequest {
  const input = recordAt(value, "INVALID_REQUEST", "The request body must be a JSON object.");
  exactKeys(input, ["messages", "instructions", "responseMode", "maxOutputTokens"], "UNSUPPORTED_FIELD");

  if (!Array.isArray(input.messages) || input.messages.length === 0 || input.messages.length > MANAGED_AI_LIMITS.maxMessages) {
    invalid("INVALID_MESSAGES", `messages must contain between 1 and ${MANAGED_AI_LIMITS.maxMessages} entries.`);
  }
  let totalCharacters = 0;
  const messages: ManagedAIMessage[] = input.messages.map((value) => {
    const message = recordAt(value, "INVALID_MESSAGES", "Each message must be an object.");
    exactKeys(message, ["role", "content"], "INVALID_MESSAGES");
    if (message.role !== "user" && message.role !== "assistant") {
      invalid("INVALID_MESSAGES", "Each message role must be user or assistant.");
    }
    const content = nonEmptyBoundedText(
      message.content,
      MANAGED_AI_LIMITS.maxMessageCharacters,
      "INVALID_MESSAGES",
      "Each message must have non-empty text content.",
    );
    totalCharacters += content.length;
    return { role: message.role, content };
  });

  let instructions: string | undefined;
  if (input.instructions !== undefined) {
    instructions = nonEmptyBoundedText(
      input.instructions,
      MANAGED_AI_LIMITS.maxInstructionsCharacters,
      "INVALID_INSTRUCTIONS",
      "instructions must be non-empty text when provided.",
    );
    totalCharacters += instructions.length;
  }
  if (totalCharacters > MANAGED_AI_LIMITS.maxTotalInputCharacters) {
    invalid("REQUEST_TOO_LARGE", "The request exceeds Doorway's managed AI input limit.");
  }

  let responseMode: ManagedAIResponseMode | undefined;
  if (input.responseMode !== undefined) {
    if (input.responseMode !== "text" && input.responseMode !== "json") {
      invalid("INVALID_RESPONSE_MODE", "responseMode must be text or json.");
    }
    responseMode = input.responseMode;
  }

  let maxOutputTokens: number | undefined;
  if (input.maxOutputTokens !== undefined) {
    if (
      !Number.isSafeInteger(input.maxOutputTokens)
      || (input.maxOutputTokens as number) < 1
      || (input.maxOutputTokens as number) > MANAGED_AI_LIMITS.maxOutputTokens
    ) {
      invalid("INVALID_OUTPUT_LIMIT", `maxOutputTokens must be between 1 and ${MANAGED_AI_LIMITS.maxOutputTokens}.`);
    }
    maxOutputTokens = input.maxOutputTokens as number;
  }

  return {
    messages,
    ...(instructions === undefined ? {} : { instructions }),
    ...(responseMode === undefined ? {} : { responseMode }),
    ...(maxOutputTokens === undefined ? {} : { maxOutputTokens }),
  };
}

function parseUsage(value: unknown): ManagedAIUsage {
  const usage = recordAt(value, "PROVIDER_RESPONSE_INVALID", "The provider returned an invalid response.");
  exactKeys(usage, ["inputTokens", "outputTokens"], "PROVIDER_RESPONSE_INVALID");
  if (
    !Number.isSafeInteger(usage.inputTokens)
    || (usage.inputTokens as number) < 0
    || !Number.isSafeInteger(usage.outputTokens)
    || (usage.outputTokens as number) < 0
  ) {
    invalid("PROVIDER_RESPONSE_INVALID", "The provider returned invalid usage data.");
  }
  return { inputTokens: usage.inputTokens as number, outputTokens: usage.outputTokens as number };
}

function isJsonValue(value: unknown, seen = new Set<object>()): value is JsonValue {
  if (value === null || typeof value === "string" || typeof value === "boolean") return true;
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value !== "object") return false;
  if (seen.has(value)) return false;
  seen.add(value);
  const valid = Array.isArray(value)
    ? value.every((item) => isJsonValue(item, seen))
    : (() => {
        const prototype = Object.getPrototypeOf(value);
        return (prototype === Object.prototype || prototype === null)
          && Object.values(value as Record<string, unknown>).every((item) => isJsonValue(item, seen));
      })();
  seen.delete(value);
  return valid;
}

function serializedLength(value: JsonValue): number {
  const serialized = JSON.stringify(value);
  return serialized?.length ?? MANAGED_AI_LIMITS.maxResponseCharacters + 1;
}

export function parseManagedAIResponse(value: unknown): ManagedAIResponse {
  const response = recordAt(value, "PROVIDER_RESPONSE_INVALID", "The provider returned an invalid response.");
  exactKeys(response, ["requestId", "output", "finishReason", "usage"], "PROVIDER_RESPONSE_INVALID");
  if (typeof response.requestId !== "string" || response.requestId.length < 1 || response.requestId.length > 128) {
    invalid("PROVIDER_RESPONSE_INVALID", "The provider returned an invalid response identifier.");
  }
  if (response.finishReason !== "stop" && response.finishReason !== "length") {
    invalid("PROVIDER_RESPONSE_INVALID", "The provider returned an invalid finish reason.");
  }
  const output = recordAt(response.output, "PROVIDER_RESPONSE_INVALID", "The provider returned an invalid output.");
  if (output.type === "text") {
    exactKeys(output, ["type", "text"], "PROVIDER_RESPONSE_INVALID");
    if (typeof output.text !== "string" || output.text.length > MANAGED_AI_LIMITS.maxResponseCharacters) {
      invalid("PROVIDER_RESPONSE_INVALID", "The provider returned invalid or oversized text.");
    }
    return {
      requestId: response.requestId,
      output: { type: "text", text: output.text },
      finishReason: response.finishReason,
      usage: parseUsage(response.usage),
    };
  }
  if (output.type === "json") {
    exactKeys(output, ["type", "value"], "PROVIDER_RESPONSE_INVALID");
    if (!isJsonValue(output.value) || serializedLength(output.value) > MANAGED_AI_LIMITS.maxResponseCharacters) {
      invalid("PROVIDER_RESPONSE_INVALID", "The provider returned invalid or oversized JSON.");
    }
    return {
      requestId: response.requestId,
      output: { type: "json", value: output.value },
      finishReason: response.finishReason,
      usage: parseUsage(response.usage),
    };
  }
  invalid("PROVIDER_RESPONSE_INVALID", "The provider returned an unsupported output type.");
}

export interface ManagedAIProviderResult {
  text: string;
  finishReason: "stop" | "length";
  usage: ManagedAIUsage;
}

export function normalizeProviderResult(
  requestId: string,
  responseMode: ManagedAIResponseMode,
  result: ManagedAIProviderResult,
): ManagedAIResponse {
  const base = {
    requestId,
    finishReason: result.finishReason,
    usage: result.usage,
  } as const;
  if (responseMode === "text") {
    return parseManagedAIResponse({ ...base, output: { type: "text", text: result.text } });
  }
  let value: unknown;
  try {
    value = JSON.parse(result.text);
  } catch {
    invalid("PROVIDER_RESPONSE_INVALID", "The provider did not return valid JSON.");
  }
  return parseManagedAIResponse({ ...base, output: { type: "json", value } });
}

export type ManagedAIModelLifecycle = "active" | "retiring";

export interface ManagedAIModelCatalogEntry {
  provider: ManagedAIProvider;
  modelId: string;
  displayName: string;
  lifecycle: ManagedAIModelLifecycle;
  capabilities: { text: true; json: boolean };
  doorwayOutputCeiling: number;
}

export const MODEL_CATALOG = [
  {
    provider: "anthropic",
    modelId: "claude-sonnet-4-5",
    displayName: "Claude Sonnet 4.5",
    lifecycle: "active",
    capabilities: { text: true, json: true },
    doorwayOutputCeiling: 4_096,
  },
  {
    provider: "anthropic",
    modelId: "claude-haiku-4-5",
    displayName: "Claude Haiku 4.5",
    lifecycle: "active",
    capabilities: { text: true, json: true },
    doorwayOutputCeiling: 4_096,
  },
  {
    provider: "anthropic",
    modelId: "claude-opus-4-1",
    displayName: "Claude Opus 4.1",
    lifecycle: "active",
    capabilities: { text: true, json: true },
    doorwayOutputCeiling: 4_096,
  },
  {
    provider: "openai",
    modelId: "gpt-5.2",
    displayName: "GPT-5.2",
    lifecycle: "active",
    capabilities: { text: true, json: true },
    doorwayOutputCeiling: 4_096,
  },
  {
    provider: "openai",
    modelId: "gpt-5-mini",
    displayName: "GPT-5 mini",
    lifecycle: "active",
    capabilities: { text: true, json: true },
    doorwayOutputCeiling: 4_096,
  },
  {
    provider: "openai",
    modelId: "gpt-5-nano",
    displayName: "GPT-5 nano",
    lifecycle: "active",
    capabilities: { text: true, json: true },
    doorwayOutputCeiling: 4_096,
  },
  {
    provider: "google",
    modelId: "gemini-3.5-flash",
    displayName: "Gemini 3.5 Flash",
    lifecycle: "active",
    capabilities: { text: true, json: true },
    doorwayOutputCeiling: 4_096,
  },
  {
    provider: "google",
    modelId: "gemini-3.1-flash-lite",
    displayName: "Gemini 3.1 Flash Lite",
    lifecycle: "active",
    capabilities: { text: true, json: true },
    doorwayOutputCeiling: 4_096,
  },
  {
    provider: "google",
    modelId: "gemini-2.5-pro",
    displayName: "Gemini 2.5 Pro",
    lifecycle: "active",
    capabilities: { text: true, json: true },
    doorwayOutputCeiling: 4_096,
  },
] as const satisfies readonly ManagedAIModelCatalogEntry[];

export function getManagedAIModel(modelId: string): ManagedAIModelCatalogEntry | undefined {
  return MODEL_CATALOG.find((entry) => entry.modelId === modelId);
}

export interface ManagedAIAdapterRequest {
  body: Record<string, JsonValue>;
}

export interface ManagedAIProviderAdapter {
  readonly provider: ManagedAIProvider;
  readonly capabilities: { text: true; json: boolean };
  createRequest(input: {
    request: ManagedAIRequest;
    model: ManagedAIModelCatalogEntry;
  }): ManagedAIAdapterRequest;
  parseResponse(input: {
    body: unknown;
    model: ManagedAIModelCatalogEntry;
    responseMode: ManagedAIResponseMode;
  }): ManagedAIProviderResult;
}

export function assertAdapterCatalogCoverage(adapters: readonly ManagedAIProviderAdapter[]): void {
  const counts = new Map<ManagedAIProvider, number>();
  for (const adapter of adapters) counts.set(adapter.provider, (counts.get(adapter.provider) ?? 0) + 1);
  for (const provider of MANAGED_AI_PROVIDERS) {
    if (counts.get(provider) !== 1) {
      invalid("INTERNAL_ERROR", "Each managed AI provider must have exactly one adapter.");
    }
  }
  for (const entry of MODEL_CATALOG) {
    const adapter = adapters.find((candidate) => candidate.provider === entry.provider);
    if (
      adapter === undefined
      || !adapter.capabilities.text
      || (entry.capabilities.json && !adapter.capabilities.json)
    ) {
      invalid("INTERNAL_ERROR", "The managed AI catalog advertises an unsupported adapter capability.");
    }
  }
}
